export interface ColumnOrder<K extends string> {
  columns: K[]
  pinned: K[]
}

export function orderColumnDefinitions<K extends string, T extends { key: K }>(
  definitions: T[],
  selected: K[],
) {
  const definitionsByKey = new Map(definitions.map((definition) => [definition.key, definition]))
  const selectedKeys = new Set(selected)
  return [
    ...selected.map((key) => definitionsByKey.get(key)).filter((definition): definition is T => Boolean(definition)),
    ...definitions.filter((definition) => !selectedKeys.has(definition.key)),
  ]
}

export function moveColumnWithinSection<K extends string>(
  order: ColumnOrder<K>,
  key: K,
  direction: -1 | 1,
): ColumnOrder<K> {
  if (order.pinned.includes(key)) {
    return { ...order, pinned: moveItem(order.pinned, key, direction) }
  }
  const section = order.columns.filter((column) => !order.pinned.includes(column))
  const nextSection = moveItem(section, key, direction)
  return { ...order, columns: replaceSection(order.columns, section, nextSection) }
}

export function dropColumnWithinSection<K extends string>(
  order: ColumnOrder<K>,
  source: K,
  target: K,
): ColumnOrder<K> {
  if (source === target) return order
  const sourcePinned = order.pinned.includes(source)
  if (sourcePinned !== order.pinned.includes(target)) return order
  if (sourcePinned) {
    return { ...order, pinned: dropBefore(order.pinned, source, target) }
  }
  const section = order.columns.filter((column) => !order.pinned.includes(column))
  const nextSection = dropBefore(section, source, target)
  return { ...order, columns: replaceSection(order.columns, section, nextSection) }
}

function moveItem<K extends string>(items: K[], key: K, direction: -1 | 1) {
  const from = items.indexOf(key)
  const to = from + direction
  if (from < 0 || to < 0 || to >= items.length) return items
  const next = [...items]
  ;[next[from], next[to]] = [next[to]!, next[from]!]
  return next
}

function dropBefore<K extends string>(items: K[], source: K, target: K) {
  if (!items.includes(source) || !items.includes(target)) return items
  const next = items.filter((item) => item !== source)
  next.splice(next.indexOf(target), 0, source)
  return next
}

function replaceSection<K extends string>(all: K[], section: K[], nextSection: K[]) {
  if (section === nextSection) return all
  const sectionKeys = new Set(section)
  let nextIndex = 0
  return all.map((key) => sectionKeys.has(key) ? nextSection[nextIndex++]! : key)
}
